/**
 * Shared cookie-consent helpers for marketing analytics gating (ENG-02).
 * Storage key + event name match the API / Cookie Policy inventory.
 *
 * GPC (DEF-US-02): honour `navigator.globalPrivacyControl` and any host-forwarded
 * Sec-GPC signal (`data-sec-gpc="1"` or `rrai-sec-gpc=1` cookie). Browsers that
 * send `Sec-GPC: 1` must also set `navigator.globalPrivacyControl` (GPC spec).
 * Analytics/marketing stay gated through this store.
 */

export const COOKIE_CONSENT_STORAGE_KEY = "rrai-cookie-consent-v1";
export const COOKIE_CONSENT_EVENT = "rrai:cookie-consent";
/** Opens the cookie preferences UI (footer “Consent preferences”, etc.). */
export const COOKIE_PREFS_OPEN_EVENT = "rrai:cookie-prefs-open";
/** Optional host-forwarded Sec-GPC cookie (static export cannot read request headers). */
export const SEC_GPC_COOKIE_NAME = "rrai-sec-gpc";

export type CookieConsentCategories = {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

const SALE_SHARE_OPT_OUT: CookieConsentCategories = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

function readSecGpcFromDocument(): boolean {
  if (typeof document === "undefined") return false;
  const root = document.documentElement;
  if (root.getAttribute("data-sec-gpc") === "1" || root.dataset.secGpc === "1") {
    return true;
  }
  try {
    return document.cookie.split(";").some((part) => {
      const [name, value] = part.trim().split("=");
      return name === SEC_GPC_COOKIE_NAME && value === "1";
    });
  } catch {
    return false;
  }
}

/**
 * True when the user has sent a Global Privacy Control opt-out.
 * Covers the JS API and a forwarded Sec-GPC header (DOM dataset / cookie).
 */
export function isGpcEnabled(): boolean {
  if (typeof navigator !== "undefined") {
    const gpc = (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl;
    if (gpc === true) return true;
  }
  return readSecGpcFromDocument();
}

/** Force analytics + marketing off while leaving necessary (and functional) intact. */
export function honorGpcCategories(
  stored: CookieConsentCategories | null,
): CookieConsentCategories {
  if (!stored) return { ...SALE_SHARE_OPT_OUT };
  return {
    necessary: true,
    functional: stored.functional,
    analytics: false,
    marketing: false,
  };
}

export function readCookieConsent(): CookieConsentCategories | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsentCategories;
    if (parsed && parsed.necessary === true) return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

export function writeCookieConsent(c: CookieConsentCategories): void {
  if (typeof window === "undefined") return;
  const next: CookieConsentCategories = isGpcEnabled()
    ? honorGpcCategories(c)
    : { ...c, necessary: true };
  try {
    localStorage.setItem(
      COOKIE_CONSENT_STORAGE_KEY,
      JSON.stringify({ ...next, ts: Date.now() }),
    );
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: next }));
  } catch {
    /* ignore */
  }
}

/**
 * Persist a GPC sale/share opt-out so the banner does not ask for a second click.
 * Returns the store after applying GPC (or the existing store when GPC is off).
 */
export function applyGpcToConsentStore(): CookieConsentCategories | null {
  if (typeof window === "undefined") return null;
  if (!isGpcEnabled()) return readCookieConsent();
  const stored = readCookieConsent();
  const next = honorGpcCategories(stored);
  if (
    !stored ||
    stored.analytics !== false ||
    stored.marketing !== false
  ) {
    writeCookieConsent(next);
  }
  return next;
}

/** Reopen the cookie banner preferences panel (does not navigate away). */
export function openCookiePreferences(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(COOKIE_PREFS_OPEN_EVENT));
}

export function hasAnalyticsConsent(): boolean {
  if (isGpcEnabled()) return false;
  return readCookieConsent()?.analytics === true;
}

export function hasMarketingConsent(): boolean {
  if (isGpcEnabled()) return false;
  return readCookieConsent()?.marketing === true;
}
